import { useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ContractContext } from "../../../../providers/contract";
import {
  AddOnFeeCategory,
  EEscalationStatus,
  ESenderRole,
  IContractEscalationDetails,
  IEscalationProposedSettlement,
  IPartyCashOut,
} from "../../../../setup/api/services/contract/models";
import SettlementModal from "../SettlementModal";
import SupportingDocumentsModal from "./SupportingDocumentsModal";
import styles from "./styles.module.scss";

const formatDate = (iso: string) =>
  new Date(iso).toLocaleString("ar-EG", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const getEscalationProposalParties = (
  proposed: IEscalationProposedSettlement | null | undefined
): IPartyCashOut[] => {
  if (!proposed) return [];
  return Array.isArray(proposed) ? proposed : proposed.parties ?? [];
};

const WAFFY_PROPOSER_LOGO_SRC = "/assets/images/svg/waffyBlue.svg";

const EscalationStatusBanner = () => {
  const { t } = useTranslation();
  const { contract } = useContext(ContractContext);
  const [openSettlementModal, setOpenSettlementModal] = useState(false);
  const [docsTicketId, setDocsTicketId] = useState<string | null>(null);

  const allProfiles = useMemo(() => {
    return [
      ...(contract?.partiesProfiles?.CUSTOMER || []).map((p) => ({
        ...p,
        roleKey: "CUSTOMER" as const,
      })),
      ...(contract?.partiesProfiles?.PROVIDER || []).map((p) => ({
        ...p,
        roleKey: "PROVIDER" as const,
      })),
      ...(contract?.partiesProfiles?.BROKER || []).map((p) => ({
        ...p,
        roleKey: "BROKER" as const,
      })),
    ];
  }, [contract?.partiesProfiles]);

  const handleSettlementButton = () => {
    setOpenSettlementModal(true);
  };

  const handleCloseSettlementModal = () => {
    setOpenSettlementModal(false);
  };

  const resolvePartyName = useCallback(
    (userId: string, userRole: ESenderRole) => {
      const match =
        allProfiles.find(
          (p) =>
            String(p.id) === userId ||
            String(p.userIdentification) === userId ||
            p.userId === userId
        ) || null;
      if (match?.profile?.fullName) return match.profile.fullName;
      const isCustomer = userRole === ESenderRole.CUSTOMER;
      if (isCustomer) return contract?.customer?.profile?.fullName || "";
      return contract?.provider?.profile?.fullName || "";
    },
    [allProfiles, contract?.customer, contract?.provider]
  );

  const resolveSettlementParties = useCallback(
    (parties: IPartyCashOut[]) => {
      const getInitials = (fullName: string) => {
        const parts = fullName.trim().split(/\s+/);
        return parts.length >= 2
          ? `${parts[0][0]}${parts[1][0]}`
          : fullName.slice(0, 2);
      };

      const itemKey = (item: IPartyCashOut) =>
        String(item.userIdentification ?? item.id);

      return parties.map((item) => {
        const key = itemKey(item);
        const match = allProfiles.find(
          (p) => String(p.userIdentification) === key || String(p.id) === key
        );
        if (match) {
          const roleLabel =
            match.roleKey === "CUSTOMER"
              ? t("lbl_buyer")
              : match.roleKey === "PROVIDER"
              ? t("lbl_seller")
              : t("lbl_broker");
          const fullName = match.profile?.fullName || key;
          return {
            name: fullName,
            roleLabel,
            amount: item.amountDue,
            fee: item.fee,
            phoneNumber: match.phoneNumber || "",
            photoUrl: match.profile?.photoUrl || "",
            initials: getInitials(fullName),
          };
        }
        if (String(contract?.customer?.userIdentification) === key) {
          const fullName = contract?.customer?.profile?.fullName || key;
          return {
            name: fullName,
            roleLabel: t("lbl_buyer"),
            amount: item.amountDue,
            fee: item.fee,
            phoneNumber: contract?.customer?.phoneNumber || "",
            photoUrl: contract?.customer?.profile?.photoUrl || "",
            initials: getInitials(fullName),
          };
        }
        if (String(contract?.provider?.userIdentification) === key) {
          const fullName = contract?.provider?.profile?.fullName || key;
          return {
            name: fullName,
            roleLabel: t("lbl_seller"),
            amount: item.amountDue,
            fee: item.fee,
            phoneNumber: contract?.provider?.phoneNumber || "",
            photoUrl: contract?.provider?.profile?.photoUrl || "",
            initials: getInitials(fullName),
          };
        }
        return {
          name: key,
          roleLabel: "",
          amount: item.amountDue,
          fee: item.fee,
          phoneNumber: "",
          photoUrl: "",
          initials: key.slice(0, 2),
        };
      });
    },
    [allProfiles, contract?.customer, contract?.provider, t]
  );

  const renderRejectionBlock = useCallback(
    (rejectionComment: string) => (
      <>
        <div className={styles.divider} />
        <div className={styles.timelineItem}>
          <div className={styles.cardHeader}>
            <div className={styles.headerMain}>
              <div className={styles.escalationLeadingSlot}>
                <span className={styles.rejectedIcon}>✕</span>
              </div>
              <div className={styles.headerInfo}>
                <span className={styles.escalatedByLabel}>
                  {t("lbl_settlement_rejected_reason")}
                </span>
              </div>
            </div>
          </div>
          <p className={styles.message}>{rejectionComment}</p>
        </div>
      </>
    ),
    [t]
  );

  const sortedEscalations = useMemo(() => {
    return [...(contract?.escalations || [])].sort(
      (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
  }, [contract?.escalations]);

  const activeEscalation = useMemo(
    () => contract?.escalations?.find((e) => !e.resolved),
    [contract?.escalations]
  );

  const isMilestoneContract = contract?.type === "MILESTONE_CONTRACT";

  if (!sortedEscalations.length || !isMilestoneContract) return null;

  const allowResolveDispute = contract?.organization?.allowResolveDispute;

  const getStatusLabel = (esc: IContractEscalationDetails) => {
    switch (esc.status) {
      case EEscalationStatus.OPEN:
        return allowResolveDispute
          ? t("lbl_escalation_open_can_propose")
          : t("lbl_waiting_waffy_settlement");
      case EEscalationStatus.RESOLVED_CUSTOMER:
      case EEscalationStatus.RESOLVED_PROVIDER:
        return t("lbl_waiting_third_party_approval");
      case EEscalationStatus.PENDING_SETTLEMENT:
        return t("lbl_escalation_cashout_pending");
      case EEscalationStatus.SETTLED:
        return t("lbl_escalation_settled_status");
      default:
        return t("lbl_escalation_under_review");
    }
  };

  const getRoleLabel = (userRole: ESenderRole) => {
    if (userRole === ESenderRole.CUSTOMER) return t("lbl_buyer");
    if (userRole === ESenderRole.PROVIDER) return t("lbl_seller");
    if (userRole === ESenderRole.ADMIN) return t("lbl_waffy");
    if (userRole === ESenderRole.BROKER) return t("lbl_broker");
    return "";
  };
  return (
    <>
      <div className={styles.wrapper}>
        <h3 className={styles.sectionTitle}>{t("lbl_escalation")}</h3>
        <div className={styles.card}>
          {sortedEscalations.map((esc, index) => {
            const isActive = !esc.resolved;
            const isNotFirst = index > 0;
            const partyName = resolvePartyName(esc.userId, esc.userRole);
            const roleLabel = getRoleLabel(esc.userRole);
            const partyLabel = partyName
              ? `${roleLabel} (${partyName})`
              : roleLabel;

            const isOpen = esc.status === EEscalationStatus.OPEN;
            const isProposed =
              esc.status === EEscalationStatus.RESOLVED_CUSTOMER ||
              esc.status === EEscalationStatus.RESOLVED_PROVIDER;
            const isPendingOrSettled =
              esc.status === EEscalationStatus.PENDING_SETTLEMENT ||
              esc.status === EEscalationStatus.SETTLED;

            const proposalParties = getEscalationProposalParties(
              esc.proposedSettlement
            );
            const settlementParties = proposalParties.length
              ? resolveSettlementParties(proposalParties)
              : [];

            const showSettlement =
              settlementParties.length > 0 &&
              (isProposed ||
                isPendingOrSettled ||
                esc.resolved ||
                !!esc.rejectionComment);

            const showRejection = !!esc.rejectionComment;
            const showResolveButton = isActive && isOpen && isMilestoneContract;

            return (
              <div
                key={esc.ticketId || index}
                className={styles.escalationGroup}>
                {isNotFirst && <div className={styles.groupDivider} />}

                {/* Escalation event */}
                <div className={styles.timelineItem}>
                  <div className={styles.cardHeader}>
                    <div className={styles.headerMain}>
                      <div className={styles.escalationLeadingSlot}>
                        <img
                          src="/assets/images/svg/escalationWarningIcon.svg"
                          alt=""
                          className={styles.warningIcon}
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display =
                              "none";
                          }}
                        />
                      </div>
                      <div className={styles.headerInfo}>
                        <span className={styles.escalatedByLabel}>
                          {t("lbl_escalated_by")}
                        </span>
                        <span className={styles.partyName}>{partyLabel}</span>
                        {esc.reason && (
                          <span className={styles.reasonLabel}>
                            {esc.reason}
                          </span>
                        )}
                        {esc.message &&
                          !esc.reason?.trim().includes(esc.message?.trim()) && (
                            <p className={styles.message}>{esc.message}</p>
                          )}
                      </div>
                    </div>
                    {esc.createdAt && (
                      <span className={styles.date}>
                        {formatDate(esc.createdAt)}
                      </span>
                    )}
                  </div>
                  {esc.supportingDocuments &&
                    esc.supportingDocuments.length > 0 && (
                      <button
                        type="button"
                        className={styles.attachmentsBtn}
                        onClick={() => setDocsTicketId(esc.ticketId)}>
                        📎 {t("lbl_attachments")} (
                        {esc.supportingDocuments.length})
                      </button>
                    )}
                </div>

                {showRejection &&
                  !isOpen &&
                  renderRejectionBlock(esc.rejectionComment as string)}

                {/* Proposed settlement */}
                {showSettlement &&
                  (() => {
                    const waffyFee = proposalParties.reduce(
                      (sum, item) => sum + (item.fee || 0),
                      0
                    );
                    const shippingFee =
                      contract?.addonFeesList?.find(
                        (f) => f.type === AddOnFeeCategory.SHIPPING_FEES
                      )?.amount || 0;
                    const serviceFee =
                      contract?.addonFeesList?.find(
                        (f) => f.type === AddOnFeeCategory.SERVICE_FEES
                      )?.amount || 0;
                    const orgName = contract?.organization?.name;

                    const feesBreakdown = [
                      ...(serviceFee > 0
                        ? [
                            {
                              label: orgName
                                ? `${t("lbl_third_party")} (${orgName})`
                                : t("lbl_third_party"),
                              amount: serviceFee,
                            },
                          ]
                        : []),
                      ...(shippingFee > 0
                        ? [
                            {
                              label: t("lbl_shipping_fees"),
                              amount: shippingFee,
                            },
                          ]
                        : []),
                      ...(waffyFee > 0
                        ? [{ label: t("lbl_waffy_fees"), amount: waffyFee }]
                        : []),
                    ];

                    const splitPhone = (phone: string) => {
                      const trimmed = phone.trim();
                      const match = trimmed.match(/^(\+\d{1,4})\s?(.*)$/);
                      return match
                        ? { code: match[1], rest: match[2] }
                        : { code: "", rest: trimmed };
                    };

                    const buyerLabel = t("lbl_buyer");

                    // updatedBy === senderId means the org's client admin proposed
                    const updatedBy = esc.proposedSettlementUpdatedBy;
                    const isClientAdminProposer =
                      !!updatedBy && updatedBy === String(contract?.senderId);
                    const proposalDate =
                      esc.proposedSettlementUpdatedAt ?? undefined;
                    const proposerName = isClientAdminProposer
                      ? contract?.organization?.name || t("lbl_client_admin")
                      : t("lbl_waffy");
                    const proposalLogoSrc = isClientAdminProposer
                      ? contract?.organization?.logoUrl ||
                        WAFFY_PROPOSER_LOGO_SRC
                      : WAFFY_PROPOSER_LOGO_SRC;

                    return (
                      <>
                        <div className={styles.divider} />
                        <div className={styles.proposalSection}>
                          <div className={styles.proposalHeader}>
                            <div className={styles.proposalHeaderInfo}>
                              <div className={styles.escalationLeadingSlot}>
                                <img
                                  src={proposalLogoSrc}
                                  alt={proposerName}
                                  className={styles.proposalProposerLogo}
                                  onError={(e) => {
                                    const el = e.target as HTMLImageElement;
                                    if (el.src.includes("waffyLogo.svg")) {
                                      el.style.display = "none";
                                      return;
                                    }
                                    el.src = WAFFY_PROPOSER_LOGO_SRC;
                                  }}
                                />
                              </div>
                              <div className={styles.proposalHeaderTexts}>
                                <span className={styles.proposalLabel}>
                                  {t("lbl_proposed_settlement")}
                                </span>
                                <span className={styles.proposalActor}>
                                  {proposerName}
                                </span>
                              </div>
                            </div>
                            {proposalDate && (
                              <span className={styles.proposalDate}>
                                {formatDate(proposalDate)}
                              </span>
                            )}
                          </div>
                          <div className={styles.partyList}>
                            {settlementParties.map((party, i) => {
                              const { code, rest } = splitPhone(
                                party.phoneNumber
                              );
                              const badgeClass =
                                party.roleLabel === buyerLabel
                                  ? styles.roleBadgeBuyer
                                  : party.roleLabel === t("lbl_broker")
                                  ? styles.roleBadgeBroker
                                  : styles.roleBadgeSeller;
                              return (
                                <div key={i} className={styles.partyRow}>
                                  <div className={styles.partyMeta}>
                                    <div
                                      className={`${styles.escalationLeadingSlot} ${styles.escalationLeadingSlotPartyAvatar}`}>
                                      <div className={styles.avatar}>
                                        {party.photoUrl ? (
                                          <img
                                            src={party.photoUrl}
                                            alt=""
                                            className={styles.avatarImg}
                                            onError={(e) => {
                                              (
                                                e.target as HTMLImageElement
                                              ).style.display = "none";
                                            }}
                                          />
                                        ) : (
                                          <span
                                            className={styles.avatarInitials}>
                                            {party.initials}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <div className={styles.partyInfo}>
                                      <div className={styles.nameAndTag}>
                                        <span className={styles.partyFullName}>
                                          {party.name}
                                        </span>
                                        {party.roleLabel && (
                                          <span
                                            className={`${styles.roleBadge} ${badgeClass}`}>
                                            {party.roleLabel}
                                          </span>
                                        )}
                                      </div>
                                      {party.phoneNumber && (
                                        <div className={styles.mobileNumber}>
                                          {code && (
                                            <span
                                              className={styles.countryCode}>
                                              {code}
                                            </span>
                                          )}
                                          <span className={styles.phoneNumber}>
                                            {rest}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <div className={styles.partyAmount}>
                                    <span className={styles.amountLabel}>
                                      {t("lbl_amount_due")}
                                    </span>
                                    <span className={styles.amountValue}>
                                      {party.amount.toFixed(0)}
                                      {t("lbl_saudi_currency")}
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          {feesBreakdown.length > 0 && (
                            <div className={styles.feesRow}>
                              {feesBreakdown.map((fee, i) => (
                                <div key={i} className={styles.feeItem}>
                                  <span className={styles.feeLabel}>
                                    {fee.label}
                                  </span>
                                  <span className={styles.feeValue}>
                                    {fee.amount.toFixed(0)}
                                    {t("lbl_saudi_currency")}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </>
                    );
                  })()}

                {showRejection &&
                  isOpen &&
                  renderRejectionBlock(esc.rejectionComment as string)}

                {/* Settlement approval */}
                {isPendingOrSettled && (
                  <>
                    <div className={styles.divider} />
                    <div className={styles.timelineItem}>
                      <div className={styles.cardHeader}>
                        <div className={styles.headerMain}>
                          <div className={styles.escalationLeadingSlot}>
                            <img
                              src="/assets/images/svg/green_tick.svg"
                              alt=""
                              className={styles.warningIcon}
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display =
                                  "none";
                              }}
                            />
                          </div>
                          <div className={styles.headerInfo}>
                            <span className={styles.escalatedByLabel}>
                              {t("lbl_escalation_approved_settlement")}
                            </span>
                            <span className={styles.partyName}>
                              {contract?.organization?.name ||
                                t("lbl_client_admin")}
                            </span>
                          </div>
                        </div>
                        {esc.resolvedAt && (
                          <span className={styles.date}>
                            {formatDate(esc.resolvedAt)}
                          </span>
                        )}
                      </div>
                    </div>
                  </>
                )}

                {/* Footer: action button or status pill */}
                <div className={styles.divider} />
                {showResolveButton ? (
                  <button
                    className={styles.resolveButton}
                    onClick={handleSettlementButton}>
                    {t("lbl_resolve_dispute")}
                  </button>
                ) : (
                  <div className={styles.statusPill}>
                    <img
                      src="/assets/images/svg/escalationStatusClock.svg"
                      alt=""
                      className={styles.clockIcon}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                    <span>{getStatusLabel(esc)}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {activeEscalation && (
        <>
          <SettlementModal
            open={openSettlementModal}
            handleClose={handleCloseSettlementModal}
            propose
          />
        </>
      )}

      {docsTicketId &&
        (() => {
          const docs = sortedEscalations.find(
            (e) => e.ticketId === docsTicketId
          )?.supportingDocuments;
          return docs?.length ? (
            <SupportingDocumentsModal
              open={true}
              onClose={() => setDocsTicketId(null)}
              documents={docs}
            />
          ) : null;
        })()}
    </>
  );
};

export default EscalationStatusBanner;
